/**
 * @file useRequestComicPage.js
 * @description 求漫页：用户提交想看的漫画留言，分页展示留言板（GUESTBOOK）
 * @module features/comic/composables/useRequestComicPage
 */

import { onActivated, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { normalizeMessageFields } from '@/features/message/utils/normalizeMessageFields'
import {
  addMessageApi,
  fetchMessagePageApi,
} from '@/features/message/api/messageApi'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'

/**
 * @param {(event: 'show-popup-message' | 'show-login-popup', ...args: unknown[]) => void} emit
 */
export function useRequestComicPage(emit) {
  const route = useRoute()
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  const newMessage = ref('')
  const messages = ref([])
  const currentPage = ref(1)
  const totalPages = ref(0)
  const total = ref(0)
  const pageSize = ref(5)
  const loading = ref(false)
  const hasMore = ref(true)
  const isMobileDevice = ref(false)

  function getAuthToken() {
    return user.value?.token || ''
  }

  function showPopupMessage(message, type) {
    emit('show-popup-message', message, type)
  }

  function showLoginPrompt() {
    emit('show-popup-message', '请您登录后操作', 'login-required')
  }

  function checkIfMobileDevice() {
    return isMobileLayoutViewport()
  }

  function resetListState() {
    currentPage.value = 1
    messages.value = []
    total.value = 0
    totalPages.value = 0
    loading.value = false
  }

  async function fetchMessages(page, force = false) {
    const validPage = Math.max(1, Number(page) || currentPage.value || 1)
    if (loading.value) {
      return
    }
    if (
      !force &&
      validPage === currentPage.value &&
      currentPage.value > 0 &&
      messages.value.length > 0
    ) {
      return
    }

    loading.value = true
    try {
      const response = await fetchMessagePageApi(
        {
          type: 'GUESTBOOK',
          pageNum: validPage,
          pageSize: pageSize.value,
        },
        getAuthToken()
      )

      if (response.data.code === 200 || response.data.code === 0) {
        const {
          contents,
          total: totalCount,
          pages,
          pageNum: responsePageNum,
        } = response.data

        messages.value = contents.map((message) => {
          const base = normalizeMessageFields(message)
          return {
            ...base,
            showReplies: false,
            replies: [],
            replyCount: base.replyCount || 0,
            existReply: base.existReply || false,
          }
        })

        total.value = totalCount || 0
        totalPages.value = pages || Math.ceil(totalCount / pageSize.value)
        currentPage.value = responsePageNum || validPage

              } else {
        showPopupMessage(response.data.msg || '加载留言失败', 'error')
      }
    } catch (error) {
      showPopupMessage(error.message || '留言接口请求失败', 'error')
    } finally {
      loading.value = false
    }
  }

  async function submitMessage() {
    if (!newMessage.value.trim()) {
      emit('show-popup-message', '请输入留言内容', 'login-required')
      return
    }

    if (!user.value?.token) {
      showLoginPrompt()
      return
    }

    try {
      const response = await addMessageApi(
        {
          content: newMessage.value.trim(),
          type: 'GUESTBOOK',
        },
        getAuthToken()
      )

      if (response.data.code === 200 || response.data.code === 0) {
        showPopupMessage(response.data.msg || '留言成功', 'success')
        newMessage.value = ''
        hasMore.value = true
        messages.value = []
        currentPage.value = 1
        totalPages.value = 0
        total.value = 0
        await fetchMessages(1)
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        showPopupMessage(response.data.msg || '留言失败', 'error')
      }
    } catch (error) {
      showPopupMessage(error.message || '留言请求失败', 'error')
    }
  }

  function handleMessagePageChange(page) {
    const pageNum = Number(page)
    const pageCurrent = Number(currentPage.value)
    const pagesTotal = Number(totalPages.value)

    if (pageNum < 1 || pageNum > pagesTotal) {
      return
    }
    if (pageNum === pageCurrent) {
      return
    }

    currentPage.value = pageNum
    fetchMessages(pageNum)
  }

  function toggleReplies(messageId) {
    const message = messages.value.find((msg) => msg.messageId === messageId)
    if (message) {
      message.showReplies = !message.showReplies
    }
  }

  async function onGuestbookMessageDeleted() {
    const p = currentPage.value
    await fetchMessages(p, true)
    if (messages.value.length === 0 && p > 1) {
      currentPage.value = p - 1
      await fetchMessages(currentPage.value, true)
    }
  }

  watch(
    () => route.name,
    (name) => {
      if (name === 'RequestComic') {
        resetListState()
        fetchMessages(1)
      }
    }
  )

  onMounted(() => {
    isMobileDevice.value = checkIfMobileDevice()
    fetchMessages(1)
  })

  onActivated(() => {
    if (messages.value.length === 0 || currentPage.value === 0) {
      resetListState()
      fetchMessages(1)
    }
  })

  return {
    newMessage,
    messages,
    currentPage,
    totalPages,
    total,
    pageSize,
    loading,
    submitMessage,
    handleMessagePageChange,
    showPopupMessage,
    onGuestbookMessageDeleted,
    toggleReplies,
  }
}
